import { create } from "zustand";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";
import {
  clipNames,
  downloadGlb,
  loadModel,
  matchBones,
  transfer,
  type BoneMatch,
} from "../lib/glb";

export type Slot = "source" | "target";

export type StatusKind = "idle" | "info" | "ok" | "warn" | "err";

export interface StatusMessage {
  kind: StatusKind;
  text: string;
}

interface SlotState {
  gltf: GLTF | null;
  fileName: string | null;
  /** The names of the model's animation clips. */
  clips: string[];
  /**
   * Per-clip checkbox state, aligned with `clips`. For the source slot a
   * checked clip transfers; for the target slot a checked clip is kept
   * (unchecked existing clips are removed from the export).
   */
  selected: boolean[];
}

interface TransferState {
  source: SlotState;
  target: SlotState;
  match: BoneMatch | null;
  status: StatusMessage;
  busy: boolean;

  /** True once both models are loaded and the export would change something. */
  ready: () => boolean;

  loadFile: (slot: Slot, file: File) => Promise<void>;
  toggleClip: (slot: Slot, index: number) => void;
  setAllClips: (slot: Slot, checked: boolean) => void;
  runTransfer: () => Promise<void>;
  reset: () => void;
}

const emptySlot: SlotState = { gltf: null, fileName: null, clips: [], selected: [] };

export const useTransferStore = create<TransferState>((set, get) => {
  const setSlot = (slot: Slot, next: SlotState) => {
    if (slot === "source") set({ source: next });
    else set({ target: next });
  };

  /** Recompute the bone match and status from the currently selected source clips. */
  const refreshMatch = () => {
    const { source, target } = get();
    if (!source.gltf || !target.gltf) {
      set({ match: null, status: { kind: "idle", text: "" } });
      return;
    }

    const clips = source.gltf.animations.filter((_, i) => source.selected[i]);
    if (clips.length === 0) {
      set({
        match: null,
        status: {
          kind: "info",
          text: "No clips selected to transfer — export only keeps/removes existing clips.",
        },
      });
      return;
    }

    const match = matchBones(clips, target.gltf);
    // e.g. Mixamo's `mixamorigHips` matched to a Ready Player Me `Hips`.
    const mapped = match.remapped ? ` · ${match.remapped} matched by rig-name mapping` : "";
    let status: StatusMessage;
    if (match.total === 0) {
      status = { kind: "warn", text: "Selected clips animate no bones." };
    } else if (match.hit === match.total) {
      status = { kind: "ok", text: `Every animated bone is present — ${match.total} bones${mapped}.` };
    } else if (match.hit === 0) {
      status = { kind: "warn", text: "No matching bone names — different skeleton?" };
    } else {
      const extra = match.total - match.hit;
      status = {
        kind: "ok",
        text: `${match.hit}/${match.total} bones present${mapped} · ${extra} unmatched track(s) will be skipped.`,
      };
    }
    set({ match, status });
  };

  return {
    source: { ...emptySlot },
    target: { ...emptySlot },
    match: null,
    status: { kind: "idle", text: "" },
    busy: false,

    ready: () => {
      const { source, target } = get();
      if (!source.gltf || !target.gltf) return false;
      // Disabled when the export would be a no-op: nothing to transfer and
      // every existing target clip kept.
      return source.selected.some(Boolean) || target.selected.some((v) => !v);
    },

    loadFile: async (slot, file) => {
      set({ status: { kind: "info", text: `Reading ${file.name}…` } });
      try {
        const { gltf } = await loadModel(file);

        if (slot === "source" && gltf.animations.length === 0) {
          set({ status: { kind: "err", text: "That file has no animation clips." } });
          return;
        }

        const clips = clipNames(gltf);
        setSlot(slot, {
          gltf,
          fileName: file.name,
          clips,
          selected: clips.map(() => true),
        });
      } catch (err) {
        set({
          status: {
            kind: "err",
            text: `${file.name}: ${err instanceof Error ? err.message : String(err)}`,
          },
        });
        return;
      }

      refreshMatch();
    },

    toggleClip: (slot, index) => {
      const state = get()[slot];
      setSlot(slot, {
        ...state,
        selected: state.selected.map((v, i) => (i === index ? !v : v)),
      });
      if (slot === "source") refreshMatch();
    },

    setAllClips: (slot, checked) => {
      const state = get()[slot];
      setSlot(slot, { ...state, selected: state.selected.map(() => checked) });
      if (slot === "source") refreshMatch();
    },

    runTransfer: async () => {
      const { source, target } = get();
      if (!source.gltf || !target.gltf || !target.fileName) return;

      const sourceClips = source.gltf.animations.filter((_, i) => source.selected[i]);
      const keepClips = target.gltf.animations.filter((_, i) => target.selected[i]);
      const removed = target.clips.length - keepClips.length;

      set({ busy: true, status: { kind: "info", text: "Baking…" } });
      try {
        const result = await transfer(
          source.gltf,
          target.gltf,
          sourceClips,
          keepClips,
          target.fileName,
        );
        downloadGlb(result.buffer, result.fileName);
        const parts: string[] = [];
        if (result.transferred) parts.push(`${result.transferred} clip(s) transferred`);
        if (result.remapped) parts.push(`${result.remapped} track(s) renamed for the target rig`);
        if (Math.abs(result.scale - 1) > 0.01) {
          parts.push(`root motion scaled ×${result.scale >= 10 ? result.scale.toFixed(0) : result.scale.toFixed(2)}`);
        }
        if (keepClips.length) parts.push(`${keepClips.length} existing kept`);
        if (removed) parts.push(`${removed} existing removed`);
        if (result.dropped) parts.push(`${result.dropped} unmatched track(s) skipped`);
        set({
          status: {
            kind: "ok",
            text: `Downloaded ${result.fileName}${parts.length ? ` · ${parts.join(" · ")}` : ""}`,
          },
        });
      } catch (err) {
        set({
          status: {
            kind: "err",
            text: `Export failed: ${err instanceof Error ? err.message : String(err)}`,
          },
        });
      } finally {
        set({ busy: false });
      }
    },

    reset: () =>
      set({
        source: { ...emptySlot },
        target: { ...emptySlot },
        match: null,
        status: { kind: "idle", text: "" },
        busy: false,
      }),
  };
});
